import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthenticationService } from '../../../../../security/authentication.service';
import { BroadcastsService } from '../../../programplan/services/broadcasts.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-transcription-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './transcription-editor.component.html',
  styleUrl: './transcription-editor.component.scss'
})
export class TranscriptionEditorComponent {
  @Input() broadcast: any;
  @ViewChild('audioPlayer') audioPlayer!: ElementRef;
  @ViewChild('videoPlayer') videoPlayer!: ElementRef;
  
  private static readonly AlleOkInfoDismissedKey = 'kiut-alle-ok-info-dismissed';

  private static readonly EpisodeIdPattern = /^\d{1,15}$/;

  T = { segments: [{ text: '' }] } as any;
  audioUrl: string | null = null;
  videoUrl: string | null = null;
  audioCurrentTime: number = 0;
  showAlleOkInfo = false;
  episodeId = '';
  episodeIdError: string | null = null;
  episodeIdSaving = false;
  transcriptError: string | null = null;
  transcriptLoaded = false;

  constructor(private broadcastsService: BroadcastsService,
              private authService: AuthenticationService,
              private http: HttpClient) {}

  ngOnInit(): void {
    this.showAlleOkInfo = localStorage.getItem(TranscriptionEditorComponent.AlleOkInfoDismissedKey) !== '1';
    this.episodeId = this.broadcast?.episodeId ?? '';
    this.loadRearrangedJson();
    this.getPresignedVideoUrl();
    this.getPresignedAudioUrl();
  }

  dismissAlleOkInfo() {
    this.showAlleOkInfo = false;
    localStorage.setItem(TranscriptionEditorComponent.AlleOkInfoDismissedKey, '1');
  }

  // Historisch importierte Archiv-Sendungen wurden ohne EpisodeID angelegt.
  // Ohne ID liefert der on-prem Poller die fertige XML nicht auf die Freigabe:
  // der Dateiname dort beginnt mit der EpisodeID.
  get isArchiveBroadcast(): boolean {
    return this.broadcast?.key?.startsWith('ARCHIV/') === true;
  }

  // Bei TvThek-Sendungen steckt die ID im Ordnernamen und bleibt unveraendert.
  get episodeIdLocked(): boolean {
    return this.broadcast?.key?.startsWith('ARCHIV/TvThek/') === true;
  }

  @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
      this.resizeAllTextareas();
    }
    
  getPresignedAudioUrl() {
    console.log('Lade Presigned URL für Audio:', this.broadcast.key.replace("broadcast.json","broadcast.wav").replace("LIVE/","VOD/"));
    this.broadcastsService.getPresignedUrlForGet(this.broadcast.key.replace("broadcast.json","broadcast.wav").replace("LIVE/","VOD/")).subscribe(result => {
      this.audioUrl = result?.data?.downloadURL;
      console.log('Presigned Audio URL:', this.audioUrl);
    });
  }

  // Store pending video keys for fallback on error
  private _pendingVideoKeys: string[] = [];
  private _pendingVideoIndex: number = 0;

  getPresignedVideoUrl() {
    if (this.broadcast.key.includes("LIVE/")) {
      // For LIVE broadcasts, the video is stored as broadcast.mp4 in the VOD folder
      const videoKey = this.broadcast.key.replace("broadcast.json", "broadcast.mp4").replace("LIVE/", "VOD/");
      console.log('Loading video for LIVE broadcast:', videoKey);
      this.broadcastsService.getPresignedUrlForGet(videoKey).subscribe(result => {
        this.videoUrl = result?.data?.downloadURL;
        console.log('Presigned Video URL:', this.videoUrl);
      });
    } else if (this.broadcast.key.startsWith("ARCHIV/")) {
      // For ARCHIV broadcasts, try multiple video key patterns
      const videoKeysToTry: string[] = [];

      if (this.broadcast.key.includes(".mp4.broadcast.json")) {
        // Pattern for keys like: title.mp4.broadcast.json -> title.mp4
        videoKeysToTry.push(this.broadcast.key.replace(".mp4.broadcast.json", ".mp4"));
      } else {
        // Patterns for keys like: title.broadcast.json
        // Try: title.broadcast.mp4
        videoKeysToTry.push(this.broadcast.key.replace(".broadcast.json", ".broadcast.mp4"));
        // Try: title.mp4
        videoKeysToTry.push(this.broadcast.key.replace(".broadcast.json", ".mp4"));
      }

      this.tryLoadVideo(videoKeysToTry, 0);
    } else {
      // For VOD broadcasts, replace .broadcast.json with .broadcast.mp4
      const videoKey = this.broadcast.key.replace(".broadcast.json", ".broadcast.mp4");
      console.log('Loading video for VOD broadcast:', videoKey);
      this.broadcastsService.getPresignedUrlForGet(videoKey).subscribe(result => {
        this.videoUrl = result?.data?.downloadURL;
        console.log('Presigned Video URL:', this.videoUrl);
      });
    }
  }

  private tryLoadVideo(videoKeys: string[], index: number) {
    if (index >= videoKeys.length) {
      console.error('Failed to load video from any location');
      return;
    }

    const videoKey = videoKeys[index];
    console.log(`Trying to load video (attempt ${index + 1}/${videoKeys.length}):`, videoKey);

    this.broadcastsService.getPresignedUrlForGet(videoKey).subscribe(result => {
      const url = result?.data?.downloadURL;
      if (url) {
        // Set the URL and let the video element handle errors via onVideoError
        this._pendingVideoKeys = videoKeys;
        this._pendingVideoIndex = index;
        this.videoUrl = url;
        console.log('Set video URL, waiting for load:', videoKey);
      } else {
        console.log(`No presigned URL for ${videoKey}, trying next...`);
        this.tryLoadVideo(videoKeys, index + 1);
      }
    });
  }

  onVideoError() {
    const nextIndex = this._pendingVideoIndex + 1;
    if (nextIndex < this._pendingVideoKeys.length) {
      console.log(`Video failed to load, trying next...`);
      this.videoUrl = null;
      this.tryLoadVideo(this._pendingVideoKeys, nextIndex);
    } else {
      console.error('All video URLs failed to load');
    }
  }

  loadRearrangedJson() {
    const keysToTry: { key: string; label: string }[] = [];

    if (this.broadcast.key.startsWith("ARCHIV/")) {
      // Archive route: try multiple path patterns
      const baseArchivKey = this.broadcast.key.replace("broadcast.json", "");
      // Get the folder path (without the filename)
      const folderPath = this.broadcast.key.substring(0, this.broadcast.key.lastIndexOf('/') + 1);

      keysToTry.push(
        // Try with filename prefix first
        { key: baseArchivKey + "transcript.corrected.json", label: "corrected with prefix (ARCHIV)" },
        { key: baseArchivKey + "transcript.rearranged.json", label: "rearranged with prefix (ARCHIV)" },
        // Try without filename prefix (just transcript.json in folder)
        { key: folderPath + "transcript.corrected.json", label: "corrected without prefix (ARCHIV)" },
        { key: folderPath + "transcript.rearranged.json", label: "rearranged without prefix (ARCHIV)" }
      );
    } else {
      // Programplan route (LIVE broadcasts): try VOD first, then LIVE
      const baseVodKey = this.broadcast.key.replace("LIVE/", "VOD/").replace("broadcast.json", "");
      const baseLiveKey = this.broadcast.key.replace("broadcast.json", "");

      keysToTry.push(
        { key: baseVodKey + "transcript.corrected.json", label: "corrected (VOD)" },
        { key: baseLiveKey + "transcript.corrected.json", label: "corrected (LIVE)" },
        { key: baseVodKey + "transcript.rearranged.json", label: "rearranged (VOD)" },
        { key: baseLiveKey + "transcript.rearranged.json", label: "rearranged (LIVE)" }
      );
    }

    this.tryLoadTranscript(keysToTry, 0);
  }

  private tryLoadTranscript(keys: { key: string; label: string }[], index: number) {
    if (index >= keys.length) {
      console.error('Failed to load transcript from any location');
      return;
    }

    const { key, label } = keys[index];
    console.log(`Trying to load transcript (${label}):`, key);

    this.broadcastsService.getPresignedUrlForGet(key).subscribe(result => {
      this.loadJsonFromPresignedUrl(result?.data?.downloadURL).then(data => {
        console.log(`Loaded transcript successfully from ${label}`);
        this.T = data;
        this.transcriptLoaded = true;
        this.resizeAllTextareas();
      }).catch(err => {
        console.log(`Transcript not found at ${label}, trying next...`);
        this.tryLoadTranscript(keys, index + 1);
      });
    });
  }

  async loadJsonFromPresignedUrl(url: string): Promise<any> {
    console.log('Lade JSON von URL:', url);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Download fehlgeschlagen: ' + response.statusText);
    }
  
    const text = await response.text();         // falls es kein valides JSON ist, kannst du hier prüfen
    return JSON.parse(text);                    // oder direkt: return await response.json();
  }

  resizeAllTextareas() {
    // Ensure all textareas resize on load
    setTimeout(() => {
      document.querySelectorAll('textarea').forEach((textarea: Element) => {
        this.autoResize(textarea as HTMLTextAreaElement);
      });
    }, 100);
  }

  autoResize(textarea: HTMLTextAreaElement) {
    if (textarea) {
      textarea.style.height = 'auto'; // Reset height first
      textarea.style.height = textarea.scrollHeight + 'px'; // Adjust to scroll height
    }
  }

  /**
   * Saves a newly entered EpisodeID first - it decides the filename the XML is
   * delivered under - and then stamps every line as reviewed and saves once.
   * Word timings are only re-interpolated for segments whose text actually
   * changed, so untouched lines keep their original per-word timing.
   */
  markAllAsOk() {
    this.episodeIdError = null;
    this.transcriptError = null;
    const episodeId = (this.episodeId ?? '').trim();

    if (episodeId && !TranscriptionEditorComponent.EpisodeIdPattern.test(episodeId)) {
      this.episodeIdError = 'EpisodeID muss eine Zahl sein.';
      return;
    }

    if (!this.needsEpisodeIdSave(episodeId)) {
      this.saveAllSegments();
      return;
    }

    this.episodeIdSaving = true;
    this.http.post(environment.API_Base + '/broadcast/update-episode-id',
                   { broadcastKey: this.broadcast.key, episodeId })
      .subscribe({
        next: () => {
          this.episodeIdSaving = false;
          this.broadcast.episodeId = episodeId;
          this.saveAllSegments();
        },
        error: err => {
          this.episodeIdSaving = false;
          this.episodeIdError = 'EpisodeID konnte nicht gespeichert werden.';
          console.error('Fehler beim Speichern der EpisodeID', err);
        }
      });
  }

  private needsEpisodeIdSave(episodeId: string): boolean {
    return !!episodeId && !this.episodeIdLocked && episodeId !== (this.broadcast?.episodeId ?? '');
  }

  private saveAllSegments() {
    // Never save the placeholder: a broadcast whose transcript could not be
    // loaded would otherwise be delivered to the share as a single empty line.
    if (!this.transcriptLoaded || !this.T?.segments?.length) {
      this.transcriptError = 'Transkript wurde nicht geladen - es wurde nichts gespeichert.';
      return;
    }

    this.transcriptError = null;

    const user = this.authService.userName();
    this.T.segments.forEach((segment: any, index: number) => {
      segment.TouchedBy = user;
      const joined = (segment.words ?? []).map((w: any) => w.word).join('');
      if (segment.text !== joined) {
        this.updateSegmentWords(index);
      }
    });

    this.updateRootText();
    this.uploadTranscript(this.T);
    this.dismissAlleOkInfo();
  }

  /**
   * Updates the words array for a segment with interpolated start/end times
   * based on the new text content.
   * Each word starts with a blank space as per the original format.
   */
  private updateSegmentWords(index: number) {
    const segment = this.T.segments[index];
    if (!segment || !segment.words || segment.words.length === 0) {
      return;
    }

    // Get the segment duration from the original words array
    const firstWordStart = segment.words[0].start;
    const lastWordEnd = segment.words[segment.words.length - 1].end;
    const segmentDuration = lastWordEnd - firstWordStart;

    // Split the new text into words, preserving leading spaces
    const newText = segment.text;
    // Split by spaces but keep the words - each word should start with a space
    const rawWords = newText.split(/(?=\s)/);

    // Filter out empty strings and ensure each word has a leading space (except possibly the first)
    let words: string[] = [];
    for (let i = 0; i < rawWords.length; i++) {
      const word = rawWords[i];
      if (word.trim().length > 0) {
        // Ensure word starts with space (except first word if it doesn't have one)
        if (i > 0 && !word.startsWith(' ')) {
          words.push(' ' + word);
        } else {
          words.push(word);
        }
      }
    }

    // If no words found, try splitting differently
    if (words.length === 0) {
      words = newText.split(' ').filter((w: string) => w.length > 0).map((w: string, i: number) => i === 0 ? w : ' ' + w);
    }

    const wordCount = words.length;
    if (wordCount === 0) {
      return;
    }

    // Calculate duration per word
    const durationPerWord = segmentDuration / wordCount;

    // Create new words array with interpolated times
    const newWords: any[] = [];
    for (let wordIndex = 0; wordIndex < wordCount; wordIndex++) {
      const wordStart = firstWordStart + (wordIndex * durationPerWord);
      const wordEnd = firstWordStart + ((wordIndex + 1) * durationPerWord);

      newWords.push({
        word: words[wordIndex],
        start: wordStart,
        end: wordEnd
      });
    }

    // Update the segment's words array
    segment.words = newWords;
  }

  /**
   * Updates the root level 'text' property by concatenating all segment texts
   */
  private updateRootText() {
    if (!this.T || !this.T.segments) {
      return;
    }

    // Concatenate all segment texts
    this.T.text = this.T.segments.map((s: any) => s.text).join('');
  }

  // Build the S3 key for the corrected transcript. Mirrors the priority-1
  // lookup in loadRearrangedJson so a save round-trips on the next load.
  // Must never equal broadcast.key — returning that would overwrite the
  // broadcast record itself (see guardrail in uploadTranscript).
  deriveCorrectedKey(): string {
    if (this.broadcast.key.startsWith("ARCHIV/")) {
      const baseArchivKey = this.broadcast.key.replace("broadcast.json", "");
      return baseArchivKey + "transcript.corrected.json";
    }
    const baseVodKey = this.broadcast.key.replace("LIVE/", "VOD/").replace("broadcast.json", "");
    return baseVodKey + "transcript.corrected.json";
  }

  uploadTranscript(transcript: any) {
    const correctedKey = this.deriveCorrectedKey();

    if (correctedKey === this.broadcast.key) {
      console.error('Refusing to upload: derived corrected key equals broadcast.key', {
        broadcastKey: this.broadcast.key,
        correctedKey
      });
      return;
    }

    console.log('Saving transcript to:', correctedKey);

    this.broadcastsService.getPresignedUrlForPut(correctedKey).subscribe(result => {
      console.log('Presigned URL:', result?.data?.uploadURL);
      this.uploadJsonToS3(result?.data?.uploadURL, transcript).subscribe({
        next: () => {
          console.log('Upload erfolgreich to', correctedKey);
          // Update TranscriptModifiedDate in the broadcast record
          this.updateTranscriptModifiedDate();
        },
        error: err => console.error('Fehler beim Upload', err)
      });
    });
  }

  private updateTranscriptModifiedDate() {
    this.http.post(environment.API_Base + '/broadcast/update-transcript-modified', { broadcastKey: this.broadcast.key })
      .subscribe({
        next: () => console.log('TranscriptModifiedDate updated'),
        error: err => console.error('Fehler beim Aktualisieren des TranscriptModifiedDate', err)
      });
  }
  
  uploadJsonToS3(presignedUrl: string, transcript: any) {
    const jsonBlob = new Blob([JSON.stringify(transcript)], {
      type: 'application/json'
    });
  
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
  
    return this.http.put(presignedUrl, jsonBlob, { headers, responseType: 'text' });
  }
  
  setCurrentAudioTime(segment: any) {
    if (segment?.words?.length > 0) {
      this.audioCurrentTime = segment.words[0].start;
    }
  }

  getCursorPosition(event: MouseEvent, index: number) {
    const target = event.target as HTMLTextAreaElement;
    const cursorPosition = target.selectionStart;
    console.log(`Cursor Position in Textarea ${index}:`, cursorPosition);

    const segment = this.T.segments[index];
    if (!segment?.words?.length || !segment?.text?.length) {
      return;
    }

    const N = segment.text.length;
    const M = segment.words.length;

    const j = Math.min(Math.floor((cursorPosition / N) * M), M - 1);
    this.audioCurrentTime = segment.words[j].start;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.code === 'AltLeft') {
      console.log('Left Alt!');
      event.preventDefault(); // Prevents default behavior like scrolling the page
      this.toggleAudio();
    }
  }

  toggleAudio() {
    const audio: HTMLAudioElement = this.audioPlayer.nativeElement;
    audio.currentTime = this.audioCurrentTime;

    const video: HTMLVideoElement = this.videoPlayer.nativeElement;
    video.currentTime = this.audioCurrentTime;

    if (video.paused) {
      //audio.play();
      video.play();
      console.log('Audio play!');
    } else {
      //audio.pause();
      video.pause();
      console.log('Audio pause!');
    }
  }
}
